import { esriRequest, Geoprocessor, RasterData, MapImageLayer } from 'cd-eft/utils/esriTypes';

export default abstract class GpApiClientBase {
  protected readonly __timeout?: number;

  constructor(timeout?: number) {
    this.__timeout = timeout;
  }

  protected async __invokeEsriRequest(
    url: string,
    method: 'post' | 'auto' | 'delete' | 'head' | 'put' | undefined,
    body: string | HTMLFormElement | FormData | undefined
  ): Promise<__esri.RequestResponse> {
    const requestOptions: __esri.RequestOptions = {
      method: method,
      body: body,
      timeout: this.__timeout,
    };
    const response = await esriRequest(url, requestOptions);
    return response;
  }

  protected async __invokeGpExecute(url: string, params: any): Promise<IGpExecuteResponse> {
    const requestOptions: any = {
      timeout: this.__timeout,
    };
    const properties: __esri.GeoprocessorProperties = {
      url,
      requestOptions,
    };
    const geoprocessor: __esri.Geoprocessor = new Geoprocessor(properties);
    const response: IGpExecuteResponse = await geoprocessor.execute(params);
    return response;
  }

  protected async __invokeGpSubmitAndWaitForCompletion(
    url: string,
    params: any,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<GpSubmitAndWaitResponse> {
    const requestOptions: any = {
      timeout: this.__timeout,
    };
    const properties: __esri.GeoprocessorProperties = {
      url,
      requestOptions,
    };

    const geoprocessor = new Geoprocessor(properties);
    const jobStart = await geoprocessor.submitJob(params);
    const jobComplete = await geoprocessor.waitForJobCompletion(jobStart.jobId, waitOptions);

    // invoke waitOptions.statusCallback one last time, since core esri library doesn't do it for you
    if (waitOptions && waitOptions.statusCallback) {
      waitOptions.statusCallback(jobComplete);
    }

    const result = new GpSubmitAndWaitResponse(geoprocessor, jobComplete);
    return result;
  }

  protected __getRasterDataProperties(rasterData: RasterData): any {
    const result: __esri.RasterDataProperties = {
      format: rasterData.format,
      itemId: rasterData.itemId,
      url: rasterData.url,
    };
    return result;
  }

  protected async __uploadFile(
    serviceUrl: string,
    file: File,
    description: string = ''
  ): Promise<Readonly<IUploadedFileInfo>> {
    const url = new URL('./uploads/upload', serviceUrl).toString();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    formData.append('f', 'json');
    const uploadFileResponse = await this.__invokeEsriRequest(url, 'post', formData);
    return uploadFileResponse.data.item as IUploadedFileInfo;
  }

  protected async __deleteFile(serviceUrl: string, fileInfo: IUploadedFileInfo): Promise<boolean> {
    const url = new URL('./uploads/{0}/delete'.replace('{0}', fileInfo.itemID), serviceUrl).toString();
    const formData = new FormData();
    formData.append('f', 'json');
    const response = await this.__invokeEsriRequest(url, 'post', formData);
    const result: boolean = response.data.success;
    return result;
  }
}

export interface IGpExecuteResponse {
  results: any[];
  messages: any[];
}

export class GpSubmitAndWaitResponse {
  private readonly __geoprocessor: Geoprocessor;
  private readonly __jobInfo: __esri.JobInfo;

  constructor(geoprocessor: Geoprocessor, jobInfo: __esri.JobInfo) {
    this.__geoprocessor = geoprocessor;
    this.__jobInfo = jobInfo;
  }

  async getResultData(resultName: string, requestOptions?: any): Promise<__esri.ParameterValue> {
    const response = await this.__geoprocessor.getResultData(this.__jobInfo.jobId, resultName, requestOptions);
    return response;
  }

  async getResultDataValue<T>(resultName: string, requestOptions?: any): Promise<T> {
    const response = await this.getResultData(resultName, requestOptions);
    return response.value;
  }

  async getResultMapImageLayer(): Promise<MapImageLayer> {
    const response = await this.__geoprocessor.getResultMapImageLayer(this.__jobInfo.jobId);
    return response;
  }
}

export interface IUploadedFileInfo {
  itemID: string;
  itemName?: string;
  description?: string;
  date?: number;
  committed?: boolean;
  serviceName?: string;
  contentType?: string;
}
